'use client';

import { ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle
} from '@components/ui/drawer';
import { useIsMobile } from '@hooks/use-mobile';
import { cn } from '@utils/cn';

interface ResponsiveOverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  contentClassName?: string;
  presentation?: 'auto' | 'sheet' | 'dialog' | 'fullscreen';
}

/**
 * The one overlay primitive screens should import: bottom sheet (Vaul) on
 * phone, centered Radix Dialog on desktop. Same open/onOpenChange contract.
 */
export function ResponsiveOverlay({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  contentClassName,
  presentation = 'auto'
}: ResponsiveOverlayProps) {
  const isMobile = useIsMobile();
  const renderSheet =
    presentation === 'sheet' || (presentation === 'auto' && isMobile);

  if (renderSheet) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className={cn('rounded-t-xl', contentClassName)}>
          <DrawerHeader className="text-left">
            <DrawerTitle>{title}</DrawerTitle>
            {description ? (
              <DrawerDescription>{description}</DrawerDescription>
            ) : null}
          </DrawerHeader>
          <div className="max-h-[70dvh] overflow-y-auto overscroll-y-contain px-4 pb-[calc(var(--safe-bottom)+1rem)]">
            {children}
            {footer ? (
              <div className="mt-4 flex flex-col-reverse gap-2">{footer}</div>
            ) : null}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          presentation === 'fullscreen' &&
            'inset-0 h-dvh max-h-none w-full max-w-none translate-x-0 translate-y-0 overflow-y-auto sm:rounded-none',
          contentClassName
        )}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        {children}
        {footer ? <DialogFooter>{footer}</DialogFooter> : null}
      </DialogContent>
    </Dialog>
  );
}
